import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const CartContext = createContext(null);
export const useCart = () => useContext(CartContext);

const round2 = (n) => Math.round(n * 100) / 100;

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try { return JSON.parse(localStorage.getItem("pb_cart")) || {}; } catch { return {}; }
  });
  const [open, setOpen] = useState(false);
  const [config, setConfig] = useState({ tax_rate: 0.07, shipping_flat: 8.99, free_shipping_threshold: 75 });

  useEffect(() => {
    api.get("/cart/config").then(({ data }) => setConfig(data)).catch(() => {});
  }, []);
  useEffect(() => { localStorage.setItem("pb_cart", JSON.stringify(items)); }, [items]);

  const addProduct = (p, variant = null) => {
    const v = variant && (variant.size || variant.color || variant.option) ? { option: variant.option || null, size: variant.size || null, color: variant.color || null } : null;
    const vl = v ? [v.option, v.size, v.color].filter(Boolean).join(" · ") : "";
    const opt = v?.option && (p.options || []).find((o) => o.label === v.option);
    const price = opt ? Number(opt.price) : p.price;
    const k = `product:${p.id}${vl ? `:${vl}` : ""}`;
    setItems((c) => ({ ...c, [k]: { key: k, type: "product", id: p.id, name: p.name, variant: v, variant_label: vl, price, image: p.image, requires_shipping: p.requires_shipping !== false, shipping_price: p.shipping_price ?? null, qty: (c[k]?.qty || 0) + 1 } }));
    toast.success(`${p.name}${vl ? ` (${vl})` : ""} added to cart`);
  };

  const addPass = (p) => {
    const k = `pass:${p.id}`;
    setItems((c) => ({ ...c, [k]: { key: k, type: "pass", id: p.id, name: p.name, price: p.price, image: null, qty: (c[k]?.qty || 0) + 1 } }));
    toast.success(`${p.name} added to cart`);
  };

  const changeQty = (key, delta) => setItems((c) => {
    const qty = (c[key]?.qty || 0) + delta;
    if (qty <= 0) { const n = { ...c }; delete n[key]; return n; }
    return { ...c, [key]: { ...c[key], qty } };
  });

  const removeItem = (key) => setItems((c) => { const n = { ...c }; delete n[key]; return n; });
  const clear = () => setItems({});

  const totals = useMemo(() => {
    const list = Object.values(items);
    const productSubtotal = round2(list.filter((i) => i.type === "product").reduce((s, i) => s + i.price * i.qty, 0));
    const shippableItems = list.filter((i) => i.type === "product" && i.requires_shipping !== false);
    const shippableSubtotal = round2(shippableItems.reduce((s, i) => s + i.price * i.qty, 0));
    const passSubtotal = round2(list.filter((i) => i.type === "pass").reduce((s, i) => s + i.price * i.qty, 0));
    const tax = round2(productSubtotal * config.tax_rate);
    const maxShipRate = shippableItems.reduce((m, i) => Math.max(m, i.shipping_price ?? config.shipping_flat), 0);
    const shipping = shippableSubtotal > 0 && shippableSubtotal < config.free_shipping_threshold ? round2(maxShipRate) : 0;
    const total = round2(productSubtotal + passSubtotal + tax + shipping);
    const count = list.reduce((s, i) => s + i.qty, 0);
    return { list, productSubtotal, shippableSubtotal, passSubtotal, tax, shipping, total, count };
  }, [items, config]);

  return (
    <CartContext.Provider value={{ ...totals, config, open, setOpen, addProduct, addPass, changeQty, removeItem, clear }}>
      {children}
    </CartContext.Provider>
  );
}
